import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { format, isValid, parse } from 'date-fns'

import { toast } from '@buffer-mono/popcorn'

import {
  parseQueryParams,
  serializeQueryParams,
} from '~publish/hooks/useQueryParam'

import {
  calculateDateRange,
  canFulfilComparison,
  parseComparisonMode,
} from '../utils'
import { usePreserveMainScroll } from './usePreserveMainScroll'

interface DateRangeSelectorResult {
  startDate: Date
  endDate: Date
  selectedDateRange: string
  onDateRangeChange: (value: string) => void
  onCustomRangeApply: (start: Date, end: Date) => void
}

const DATE_FORMAT = 'yyyy-MM-dd'

function toScalar(
  value:
    | string
    | string[]
    | null
    | undefined
    | Array<string | string[] | null | undefined>,
): string | undefined {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    const first = value[0]
    return typeof first === 'string' ? first : undefined
  }
  return undefined
}

/**
 * Changing the primary period always clears the active comparison (see the
 * handlers below). When the comparison the user had selected genuinely can't
 * fit the new period — a custom window of the wrong length, or
 * previous-week/month on too long a range — surface a toast so the reset isn't
 * silent. Presets that still fit (previous-period/year) are cleared quietly.
 * Uses the same {@link canFulfilComparison} check as the selector's fallback.
 */
function warnIfComparisonDropped(
  params: ReturnType<typeof parseQueryParams>,
  newPrimaryStart: Date,
  newPrimaryEnd: Date,
): void {
  const comparisonParam = toScalar(params.comparison)
  // No param means the default comparison, which has nothing to lose.
  if (!comparisonParam) return

  const mode = parseComparisonMode(comparisonParam)
  let customStart: Date | undefined
  let customEnd: Date | undefined
  if (mode === 'custom') {
    const startParam = toScalar(params.comparisonStartDate)
    const endParam = toScalar(params.comparisonEndDate)
    if (startParam && endParam) {
      const start = parse(startParam, DATE_FORMAT, new Date())
      const end = parse(endParam, DATE_FORMAT, new Date())
      if (isValid(start) && isValid(end)) {
        customStart = start
        customEnd = end
      }
    }
  }

  if (
    !canFulfilComparison(
      mode,
      newPrimaryStart,
      newPrimaryEnd,
      customStart,
      customEnd,
    )
  ) {
    toast.warning('Comparison Reset', {
      description: "The updated date filter doesn't fit the comparison period.",
    })
  }
}

export function useDateRangeSelector(): DateRangeSelectorResult {
  const location = useLocation()
  const navigate = useNavigate()
  const captureScrollBeforeNavigate = usePreserveMainScroll()

  const raw = parseQueryParams(location.search)
  const requestedDateRange = toScalar(raw.dateRange) ?? 'last-30-days'
  const startParam = toScalar(raw.startDate)
  const endParam = toScalar(raw.endDate)

  const { startDate, endDate, selectedDateRange } = useMemo(() => {
    if (requestedDateRange !== 'custom') {
      return {
        ...calculateDateRange(requestedDateRange),
        selectedDateRange: requestedDateRange,
      }
    }
    if (startParam && endParam) {
      const start = parse(startParam, DATE_FORMAT, new Date())
      const end = parse(endParam, DATE_FORMAT, new Date())
      if (isValid(start) && isValid(end) && start <= end) {
        return { startDate: start, endDate: end, selectedDateRange: 'custom' }
      }
    }
    // A custom range without usable dates — the picker is still open, or the
    // URL was hand-edited. Only the queried window falls back; the selection
    // stays 'custom' so the toggle keeps highlighting the requested range.
    return {
      ...calculateDateRange('last-30-days'),
      selectedDateRange: 'custom',
    }
  }, [requestedDateRange, startParam, endParam])

  const handleDateRangeChange = useCallback(
    (value: string): void => {
      const params = { ...parseQueryParams(location.search) }
      if (value !== 'custom') {
        delete params.startDate
        delete params.endDate
        // Warn before clearing the comparison if the new preset range can't
        // fit it. (The `custom` branch defers to `handleCustomRangeApply`,
        // which knows the picked range.)
        const { startDate: newStart, endDate: newEnd } =
          calculateDateRange(value)
        warnIfComparisonDropped(params, newStart, newEnd)
      }
      // Reset the comparison whenever the primary period changes — a
      // comparison anchored to the previous period rarely makes sense in
      // the new one, and silently keeping it confused users.
      delete params.comparison
      delete params.comparisonStartDate
      delete params.comparisonEndDate
      params.dateRange = value
      captureScrollBeforeNavigate()
      navigate({ search: serializeQueryParams(params) }, { replace: true })
    },
    [location.search, navigate, captureScrollBeforeNavigate],
  )

  const handleCustomRangeApply = useCallback(
    (start: Date, end: Date): void => {
      const params = { ...parseQueryParams(location.search) }
      // Warn before clearing the comparison if the picked range can't fit it.
      warnIfComparisonDropped(params, start, end)
      delete params.comparison
      delete params.comparisonStartDate
      delete params.comparisonEndDate
      params.startDate = format(start, DATE_FORMAT)
      params.endDate = format(end, DATE_FORMAT)
      params.dateRange = 'custom'
      captureScrollBeforeNavigate()
      navigate({ search: serializeQueryParams(params) }, { replace: true })
    },
    [location.search, navigate, captureScrollBeforeNavigate],
  )

  return {
    startDate,
    endDate,
    selectedDateRange,
    onDateRangeChange: handleDateRangeChange,
    onCustomRangeApply: handleCustomRangeApply,
  }
}
